import React from "react";
import * as SubframeCore from "@subframe/core";
import { Badge } from "subframe/components/Badge";
import { Accordion } from "subframe/components/Accordion";
import { BadgeVariant } from "../../../types/common";

interface CategoryQuestion {
  title: string;
  question: string;
  score: number;
}

interface CategoryItem {
  category: string;
  score: number;
  response_count: number;
  weightage: number;
  questions: CategoryQuestion[];
}

interface CategoryAnalysisData {
  areas_of_concern: CategoryItem[];
  neutral_areas: CategoryItem[];
  strengths: CategoryItem[];
}

interface CategoryAnalysisProps {
  employeeId?: number;
  categoryAnalysisData?: any;
  categoryAnalysisLoading?: boolean;
}

export const CategoryAnalysis: React.FC<CategoryAnalysisProps> = ({ 
  employeeId,
  categoryAnalysisData,
  categoryAnalysisLoading
}) => {
  if (categoryAnalysisLoading) {
    return (
      <div className="flex w-full flex-col items-start gap-3 sm:gap-4 overflow-y-auto">
        <div className="flex w-full items-center justify-center py-8">
          <span className="text-body font-body text-subtext-color">Loading category analysis...</span>
        </div>
      </div>
    );
  }

  if (!categoryAnalysisData) {
    return (
      <div className="flex w-full flex-col items-start gap-3 sm:gap-4 overflow-y-auto">
        <div className="flex w-full items-center justify-center py-8">
          <span className="text-body font-body text-subtext-color">Unable to load category analysis</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col items-start gap-3 sm:gap-4 overflow-y-auto">
      {/* Areas of Concern */}
      {categoryAnalysisData.areas_of_concern && categoryAnalysisData.areas_of_concern.length > 0 && (
        <div className="flex w-full flex-col items-start gap-3 sm:gap-4">
          <div className="flex w-full items-center gap-2">
            <SubframeCore.Icon
              className="text-body font-body text-error-600"
              name="FeatherAlertCircle"
            />
            <span className="text-subheader font-subheader text-error-600">
              Areas of Concern
            </span>
          </div>
          <div className="flex w-full flex-col items-start gap-2">
            {[...categoryAnalysisData.areas_of_concern]
              .sort((a, b) => b.score - a.score)
              .map((category: CategoryItem, index: number) => (
                <CategoryAccordion
                  key={index}
                  title={category.category}
                  score={category.score.toString()}
                  variant="error"
                  items={[...category.questions]
                    .sort((a, b) => b.score - a.score)
                    .map((q: CategoryQuestion) => ({
                      day: q.title,
                      question: q.question,
                      score: q.score
                    }))}
                />
              ))}
          </div>
        </div>
      )}

      {/* Neutral Areas */}
      {categoryAnalysisData.neutral_areas && categoryAnalysisData.neutral_areas.length > 0 && (
        <div className="flex w-full flex-col items-start gap-3 sm:gap-4">
          <div className="flex w-full items-center gap-2">
            <SubframeCore.Icon
              className="text-body font-body text-warning-600"
              name="FeatherAlertTriangle"
            />
            <span className="text-subheader font-subheader text-warning-600">
              Neutral Areas
            </span>
          </div>
          <div className="flex flex-col w-full items-start gap-2">
            {[...categoryAnalysisData.neutral_areas]
              .sort((a, b) => b.score - a.score)
              .map((category: CategoryItem, index: number) => (
                <CategoryAccordion
                  key={index}
                  title={category.category}
                  score={category.score.toString()}
                  variant="warning"
                  items={[...category.questions]
                    .sort((a, b) => b.score - a.score)
                    .map((q: CategoryQuestion) => ({
                      day: q.title,
                      question: q.question,
                      score: q.score
                    }))}
                />
              ))}
          </div>
        </div>
      )}

      {/* Strengths */}
      {categoryAnalysisData.strengths && categoryAnalysisData.strengths.length > 0 && (
        <div className="flex w-full flex-col items-start gap-3 sm:gap-4">
          <div className="flex w-full items-center gap-2">
            <SubframeCore.Icon
              className="text-body font-body text-success-600"
              name="FeatherCheckCircle"
            />
            <span className="text-subheader font-subheader text-success-600">
              Strengths
            </span>
          </div>
          <div className="flex w-full flex-col items-start gap-2">
            {[...categoryAnalysisData.strengths]
              .sort((a, b) => b.score - a.score)
              .map((category: CategoryItem, index: number) => (
                <CategoryAccordion
                  key={index}
                  title={category.category}
                  score={category.score.toString()}
                  variant="success"
                  items={[...category.questions]
                    .sort((a, b) => b.score - a.score)
                    .map((q: CategoryQuestion) => ({
                      day: q.title,
                      question: q.question,
                      score: q.score
                    }))}
                />
              ))}
          </div>
        </div>
      )}

      {/* No Data State */}
      {(!categoryAnalysisData.areas_of_concern || categoryAnalysisData.areas_of_concern.length === 0) &&
       (!categoryAnalysisData.neutral_areas || categoryAnalysisData.neutral_areas.length === 0) &&
       (!categoryAnalysisData.strengths || categoryAnalysisData.strengths.length === 0) && (
        <div className="flex w-full items-center justify-center py-8">
          <span className="text-body font-body text-subtext-color">No category analysis available</span>
        </div>
      )}
    </div>
  );
};

interface CategoryAccordionProps {
  title: string;
  score: string;
  variant: 'error' | 'warning' | 'success';
  items: Array<{
    day?: string;
    question: string;
    score: number;
  }>;
}

const CategoryAccordion: React.FC<CategoryAccordionProps> = ({ title, score, variant, items }) => {
  const getVariantConfig = (variant: string) => {
    switch (variant) {
      case 'error':
        return {
          borderColor: 'border-error-200',
          bgColor: 'bg-[#fffafaff]',
          badgeVariant: BadgeVariant.ERROR as "error"
        };
      case 'warning':
        return {
          borderColor: 'border-warning-400',
          bgColor: 'bg-[#fffef5ff]',
          badgeVariant: BadgeVariant.WARNING as "warning"
        };
      case 'success':
        return {
          borderColor: 'border-success-500',
          bgColor: 'bg-[#fafffcff]',
          badgeVariant: BadgeVariant.SUCCESS as "success"
        };
      default:
        return {
          borderColor: 'border-error-200',
          bgColor: 'bg-[#fffafaff]',
          badgeVariant: BadgeVariant.ERROR as "error"
        };
    }
  };

  const config = getVariantConfig(variant);

  return (
    <div className="flex w-full min-w-0 flex-col items-start gap-4 sm:gap-6 shadow-sm">
      <Accordion
        trigger={
          <div className={`flex w-full items-center justify-between border-l-4 border-solid ${config.borderColor} ${config.bgColor} pl-2 pr-2 sm:pl-4 sm:pr-4 py-2`}>
            <div className="flex grow shrink-0 basis-0 flex-col items-start justify-between rounded-full px-2 sm:px-4 py-1">
              <div className="flex w-full sm:items-center justify-between gap-1">
                <span className="text-body font-body text-default-font text-sm sm:text-base break-words">
                  {title}
                </span>
                <Badge variant={config.badgeVariant} className="text-xs sm:text-sm rounded-md">{score}</Badge>
              </div>
            </div>
            <Accordion.Chevron />
          </div>
        }
      >
        <div className="flex w-full grow shrink-0 basis-0 flex-col items-start gap-2 border-t border-solid border-neutral-border px-2 sm:px-4 py-3 sm:py-4">
          <div className="flex flex-col items-start gap-2 w-full">
            {items.map((item, index) => (
              <div key={index} className="flex w-full flex-col sm:flex-row items-start gap-2">
                {/* Left: day badge above question */}
                <div className="flex flex-col flex-1 min-w-0">
                  {item.day && (
                    <Badge
                      variant="neutral"
                      className="h-6 flex-shrink-0 text-xs sm:text-sm mb-1 max-w-[fit-content]"
                    >
                      {item.day}
                    </Badge>
                  )}
                  <span className="text-body font-body text-neutral-900 break-words text-sm sm:text-base">
                    {item.question}
                  </span>
                </div>
                {/* Right: score badge */}
                <Badge
                  variant={config.badgeVariant}
                  className="flex-shrink-0 text-xs sm:text-sm px-2 rounded-md"
                >
                  {item.score}
                </Badge>
              </div>
            ))}
          </div>
        </div>
      </Accordion>
    </div>
  );
}; 